import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, computed, effect, inject, input, OnDestroy, OnInit, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Subscription } from 'rxjs';

import { BaasI18n } from '@lvflanders/bar';
import { BarCoreService } from '@lvflanders/bar-ng';

import { BaasLoading } from '../../baas-loading/baas-loading';
import { BaasLoadingOptions, createDefaultBaasLoadingOptions } from '../../baas-loading/baas-loading-options.type';
import { BaasToastMessageService } from '../../toast-messages/baas-toast-message-service';
import { BaasToastMessages } from '../../toast-messages/baas-toast-messages/baas-toast-messages';
import { BaasGlobalFooter } from '../baas-global-footer';
import { BaasGlobalHeader } from '../baas-global-header';
import { _BaasInternalLayoutService } from '../baas-internal-layout.service';
import { BaasLayoutService } from '../baas-layout/baas-layout.service';
import { BarDeviceType } from '../bar-device-type.enum';
import { BarLayoutInternalGvkService } from '../bar-layout-internal-gvk.service';
import { BarLayoutState } from '../bar-layout-state.contract';
import { BarResponsiveOptions } from '../bar-responsive-options.type';
import { BAAS_LAYOUT_STATE } from '../layout-tokens';

@Component({
    selector: 'baas-shell',
    templateUrl: './baas-shell.html',
    host: { class: 'baas-shell' },
    imports: [BaasGlobalHeader, BaasGlobalFooter, BaasToastMessages, BaasLoading]
})
export class BaasShell implements OnInit, OnDestroy {
    readonly #layoutState = inject<BarLayoutState>(BAAS_LAYOUT_STATE);
    readonly #core = inject(BarCoreService);
    readonly #i18n = inject(BaasI18n);
    readonly #toastMessageService = inject(BaasToastMessageService);
    readonly #internalLayout = inject(_BaasInternalLayoutService);
    readonly #layout = inject(BaasLayoutService);
    readonly #breakpointObserver = inject(BreakpointObserver);
    readonly #gvk = inject(BarLayoutInternalGvkService);

    readonly _showHeaderFooter = input<boolean>(undefined, { alias: 'showHeaderFooter' });
    protected readonly showHeaderFooter = computed(() => {
        if (this._showHeaderFooter() !== undefined) return this._showHeaderFooter();
        return !this.#layout.compact() && !this.#layout.touch();
    });

    readonly _loading = input<boolean>(false, { alias: 'loading' });
    protected readonly loading = computed(() => {
        if (this.#layout.loading() !== undefined) return this.#layout.loading();
        return this._loading();
    });

    readonly _loadingOptions = input<BaasLoadingOptions>(undefined, { alias: 'loading' });
    protected readonly loadingOptions = computed(() => {
        if (this.#layout.loadingOptions() !== undefined) return this.#layout.loadingOptions();
        else if (this._loadingOptions() !== undefined) return this._loadingOptions();
        return createDefaultBaasLoadingOptions();
    });

    readonly _appLoading = input<boolean>(false, { alias: 'appLoading' });
    protected readonly appLoading = computed(() => {
        if (this.#layout.appLoading() !== undefined) return this.#layout.appLoading();
        return this._appLoading();
    });

    /** @experimental */
    readonly responsive = input<BarResponsiveOptions>({ enabled: false });

    #breakpointObserverSubscription: Subscription;

    constructor() {
        effect(() => {
            if (this._loading() !== undefined) this.#layout.loading.set(this._loading());
        });
        effect(() => {
            if (this._loadingOptions() !== undefined) this.#layout.loadingOptions.set(this._loadingOptions());
        });

        effect(() => {
            const appLoading = this.appLoading();
            untracked(() => {
                this.#defineAppLoading(appLoading);
            });
        });

        effect(() => {
            const touch = this.#layout.touch();
            untracked(() => {
                const bodyTouch = document.body.classList.contains('touch');
                if (touch && !bodyTouch) document.body.classList.add('touch');
                else if (!touch && bodyTouch) document.body.classList.remove('touch');
            });
        });

        this.#core.errorOccurred.pipe(takeUntilDestroyed()).subscribe(() => {
            this.#toastMessageService.addMessage({
                text: this.#i18n.get('DOMAIN_BAAS_CORE.GENERIC_ERROR'),
                type: 'error'
            });
        });

        effect(() => {
            const compact = this.#layout.compact();
            untracked(() => {
                // whenever compact changes: persist it to backend
                if (compact !== null) this.#gvk.persistPageState(compact ? 'compact' : 'default');
            });
        });

        // hook to gvk internal compact changed because we don't want cyclic dependency
        this.#gvk.compactChanged.subscribe((compact) => {
            // when gvk value is loaded from backend we assign it
            this.#layout.compact.set(compact);
        });
        // hook to gvk internal menu state changed because we don't want cyclic dependency
        this.#gvk.barMenuStateChanged.pipe(takeUntilDestroyed()).subscribe((menuState) => {
            // when gvk value is loaded from backend we assign it
            this.#layout.menuState.set(menuState);
        });
    }

    ngOnInit(): void {
        const element = document.getElementById('favicon');
        element.setAttribute('href', this.#layoutState.favIconUrl);

        if (this.#layoutState.environment) {
            document.body.classList.add(this.#layoutState.environment);
        }
        if (this.#layout.appVersion() === undefined && this.#layoutState.appVersion) {
            this.#layout.appVersion.set(this.#layoutState.appVersion);
        }
        this.#initBreakpointObserver();
        document.title = this.#layoutState.documentTitle;

        this.#gvk.init();
    }

    ngOnDestroy(): void {
        if (this.#breakpointObserverSubscription) this.#breakpointObserverSubscription.unsubscribe();
    }

    #initialAppLoader = true;
    /** Defines the app loading whereby the app loader appears. */
    #defineAppLoading(value: boolean): void {
        const loader = document.getElementById('app-loading');
        if (loader) {
            const appRootElements = document.getElementsByTagName('app-root');
            const appRoot = appRootElements.length ? <HTMLElement>appRootElements[0] : null;

            if (!value) {
                this.#initialAppLoader = false;
                loader.setAttribute('class', 'app-loading-fade-out');
                if (appRoot) appRoot.removeAttribute('style');
                // after the animation of 500ms we remove the loader as it stack above the content and is no longer needed
                setTimeout(() => {
                    loader.style.display = 'none';
                    loader.removeAttribute('class');
                    document.body.style.overflow = 'unset';
                }, 500);
            } else if (!this.#initialAppLoader) {
                loader.removeAttribute('class');
                loader.style.display = '';
                // hide app root because it is rendered underneath the app-loading
                if (appRoot) appRoot.style.display = 'none';
            }
        }
    }

    #initBreakpointObserver(): void {
        if (!this.responsive()?.enabled) return;

        const mobileMaxWidth = this.responsive().mobileWidth || 768;
        const tabletMaxWidth = this.responsive().tabletWidth || 991;
        if (tabletMaxWidth <= mobileMaxWidth)
            console.error(`Tablet width (${tabletMaxWidth} px) cannot be smaller or equal to the mobile width (${mobileMaxWidth} px).`);
        const tabletMinWidth = mobileMaxWidth + 1;

        const mobileWidthExpression = `(max-width: ${mobileMaxWidth}px)`;
        const tabletWidthExpression = `(min-width: ${tabletMinWidth}px) and (max-width: ${tabletMaxWidth}px)`;

        this.#breakpointObserverSubscription = this.#breakpointObserver.observe([mobileWidthExpression, tabletWidthExpression]).subscribe((result) => {
            const [isMobile, isTablet] = [result.breakpoints[mobileWidthExpression], result.breakpoints[tabletWidthExpression]];

            const deviceType = isMobile ? BarDeviceType.Mobile : isTablet ? BarDeviceType.Tablet : BarDeviceType.Desktop;
            this.#internalLayout.deviceType.set(deviceType);
            const touch = isMobile || isTablet;
            this.#layout.touch.set(touch);
        });
    }
}
